import React, {
  ReactNode, useEffect, useRef, useState,
} from "react"
import {
  Box, Flex, Heading, Icon as ChakraIcon, SimpleGrid,
} from "@chakra-ui/react"
import _ from "lodash"
import Button from "components/Buttons/Button"
import { EventPhase } from "../phase"
import {
  BlockId, Layout, defaultLayout, isDefaultLayout, moveBlock, resolveLayout,
} from "../layout"
import CustomizePanel from "./CustomizePanel"

const LayoutIcon = () => (
  <ChakraIcon
    viewBox="0 0 24 24"
    boxSize={3.5}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    data-icon="layout"
    aria-hidden
  >
    <rect x={3} y={4} width={18} height={16} rx={2} />
    <path d="M3 10h18M12 10v10" />
  </ChakraIcon>
)

type Props = {
  phase: EventPhase
  // A falsy block is one the user can't see.
  blocks: Partial<Record<BlockId, ReactNode>>
  // Each phase's saved layout, as stored.
  saved?: Partial<Record<EventPhase, unknown>>
  // The phases changed while customizing; null sends a phase back to its default.
  onSave: (changes: Partial<Record<EventPhase, Layout | null>>) => void
  canCustomize?: boolean
  onCustomizingChange?: (customizing: boolean) => void
}

const CustomizableGrid = ({
  phase, blocks, saved, onSave, canCustomize = true, onCustomizingChange,
}: Props) => {
  // One draft per phase edited in the panel; null while not customizing.
  const [drafts, setDrafts] = useState<Partial<Record<EventPhase, Layout>> | null>(null)
  const [shownPhase, setShownPhase] = useState(phase)
  const [highlighted, setHighlighted] = useState<BlockId | null>(null)
  const header = useRef<HTMLDivElement>(null)
  const grid = useRef<HTMLDivElement>(null)
  const wasEditing = useRef(false)
  const editing = !!drafts
  const shown = editing ? shownPhase : phase
  const layout = drafts?.[shown] ?? resolveLayout(saved?.[shown], shown)
  const available = layout.order.filter((id) => !!blocks[id])
  const visible = available.filter((id) => !layout.hidden.includes(id))
  const hasChanges = Object.entries(drafts ?? {}).some(([edited, draft]) => (
    !_.isEqual(draft, resolveLayout(saved?.[edited as EventPhase], edited as EventPhase))
  ))

  const update = (change: (current: Layout) => Layout) => setDrafts((current) => current && {
    ...current, [shown]: change(current[shown] ?? resolveLayout(saved?.[shown], shown)),
  })
  const toggle = (id: BlockId) => update((current) => ({
    ...current,
    hidden: current.hidden.includes(id)
      ? current.hidden.filter((other) => other !== id)
      : [...current.hidden, id],
  }))
  const move = (id: BlockId, target: BlockId) => update((current) => ({
    ...current, order: moveBlock(current.order, id, target),
  }))
  const open = () => {
    setShownPhase(phase)
    setDrafts({})
  }
  const close = () => {
    setDrafts(null)
    setHighlighted(null)
  }

  // Also ends when the page rebuilds the grid mid-customize (the event's phase changed),
  // so the page never stays dimmed.
  useEffect(() => {
    if (!editing) return undefined

    onCustomizingChange?.(true)

    return () => onCustomizingChange?.(false)
  }, [editing, onCustomizingChange])
  // Back to Customize once it's enabled again, as the panel that had focus is gone.
  useEffect(() => {
    if (wasEditing.current && !editing) header.current?.querySelector("button")?.focus()
    wasEditing.current = editing
  }, [editing])
  // Once the pointer rests on a row, so moving down the list doesn't scroll through every card.
  useEffect(() => {
    const timer = setTimeout(() => {
      grid.current?.querySelector(`[data-testid="dashboard-block-${highlighted}"]`)
        ?.scrollIntoView?.({ block: "nearest", behavior: "smooth" })
    }, 300)

    return () => clearTimeout(timer)
  }, [highlighted])
  const done = () => {
    if (drafts && Object.keys(drafts).length) {
      onSave(_.mapValues(drafts, (draft, edited) => (
        draft && isDefaultLayout(draft, edited as EventPhase) ? null : draft
      )) as Partial<Record<EventPhase, Layout | null>>)
    }
    close()
  }

  return (
    <>
      <Flex ref={header} align="center" justify="space-between" gap={4} mt={10} mb={4}>
        <Heading fontSize="2xl" fontWeight="semibold" letterSpacing="tight">
          Event Details
        </Heading>
        {canCustomize && (
          <Button
            size="sm"
            variant="outline"
            display={{ base: "none", lg: "inline-flex" }}
            leftIcon={<LayoutIcon />}
            isDisabled={editing}
            onClick={open}
          >
            Customize
          </Button>
        )}
      </Flex>
      <SimpleGrid
        ref={grid}
        columns={{ base: 1, md: 2, xl: 3 }}
        spacing={8}
        sx={{ gridAutoFlow: editing ? "row" : "row dense" }}
      >
        {visible.map((id) => (
          <Box
            key={id}
            data-testid={`dashboard-block-${id}`}
            data-highlighted={highlighted === id ? "" : undefined}
            position="relative"
            minW={0}
            // Inert while customizing: the cards' links and buttons would otherwise
            // still take clicks and could leave the page.
            {...(editing && { inert: "" })}
            _after={highlighted === id ? {
              content: "\"\"",
              position: "absolute",
              inset: 0,
              borderRadius: "md",
              border: "2px solid",
              borderColor: "blue.500",
              pointerEvents: "none",
            } : undefined}
          >
            {blocks[id]}
          </Box>
        ))}
      </SimpleGrid>
      {editing && (
        <CustomizePanel
          phase={shown}
          onPhaseChange={setShownPhase}
          ids={available}
          hidden={layout.hidden}
          onToggle={toggle}
          onMove={move}
          onHighlight={setHighlighted}
          onReset={() => setDrafts((current) => ({ ...current, [shown]: defaultLayout(shown) }))}
          onCancel={close}
          onDone={done}
          hasChanges={hasChanges}
        />
      )}
    </>
  )
}

export default CustomizableGrid
